@use "@styles/tokens";
@use "./tooltipSurface" as tooltip-surface;

// Visual source of truth: docs/specs/2026-09-28-tooltip-figma-rework.md
// (Figma Tooltip set 3971:1089, "Geometry and DOM", "Motion").

@layer base-components {
    // Chrome sends no pointer events to a disabled form control, so hover
    // lands on this wrapper instead; the control is inert anyway.
    // `display: inline` keeps the wrapper out of layout, so a full-width or
    // truncating child sizes against its real container.
    .disabledTrigger {
        display: inline;

        > :disabled {
            pointer-events: none;
        }
    }

    .positioner {
        // Kept from the legacy tooltip: above modals (1050) and menus (1065).
        z-index: 10000;
    }

    .popup {
        @include tooltip-surface.surface;

        display: flex;
        flex-direction: column;
        gap: var(--space-4);
        box-sizing: border-box;

        // ds-deviation: 300px is Figma's fixed tooltip width, used as a cap;
        // --tooltip-max-width is the listed override for wide content.
        max-width: min(var(--available-width), var(--tooltip-max-width, 300px));
        text-align: left;

        // File names and links have no spaces to wrap at.
        overflow-wrap: anywhere;
        transform-origin: var(--transform-origin);
        transition:
            opacity var(--duration-medium) var(--ease-out),
            transform var(--duration-medium) var(--ease-out);

        &[data-starting-style],
        &[data-ending-style] {
            opacity: 0;
            transform: scale(0.97);
        }

        &[data-ending-style] {
            transition-duration: var(--duration-fast);
        }

        &[data-instant] {
            transition-duration: 0ms;
        }

        @include tokens.reduced-motion {
            &[data-starting-style],
            &[data-ending-style] {
                transform: none;
            }
        }
    }

    .shortcutRow {
        flex-direction: row;
        gap: var(--space-8);
        align-items: center;
        padding: var(--space-4) var(--space-4) var(--space-4) var(--space-8);

        .content {
            white-space: nowrap;
        }
    }

    .shortcutOnly {
        flex-direction: row;
        padding: var(--space-4);
    }

    .title {
        @include tokens.text-xs-12-medium;

        margin: 0;
    }

    .bullets {
        margin: 0;
        padding: 0;
        list-style: none;

        > li {
            position: relative;
            padding-inline-start: var(--space-10);

            &::before {
                content: "•";
                position: absolute;
                inset-inline-start: 0;
            }
        }
    }

    // Label and value rows, label in the popup's --fg-secondary and value in
    // --fg-primary, its `/ detail` in --fg-tertiary; sections split by a
    // divider line.
    .sections {
        display: flex;
        flex-direction: column;
        gap: var(--space-4);
    }

    .section {
        display: flex;
        flex-direction: column;
        gap: var(--space-4);
        margin: 0;
    }

    .sectionDivider {
        margin: 0;
        border: 0;
        border-top: var(--border-width) solid var(--border-divider);
    }

    .row {
        display: flex;
        gap: var(--space-12);
        align-items: baseline;
        justify-content: space-between;
        min-width: 0;
    }

    .rowLabel {
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .rowValue {
        flex-shrink: 0;
        margin: 0;
        color: var(--fg-primary);
        font-variant-numeric: tabular-nums;
        white-space: nowrap;
    }

    .rowDetail {
        color: var(--fg-tertiary);
    }

    // Presence rows (Figma 4822:8969 and 4822:9018, spec "Avatar stack and
    // presence tooltip") on the regular popup surface: a 4px dot, then
    // `Name · Detail` in --fg-primary with a --fg-tertiary separator.
    .presence {
        color: var(--fg-primary);
    }

    .presenceRows {
        display: flex;
        flex-direction: column;
        gap: var(--space-4);
        margin: 0;
        padding: 0;
        list-style: none;
    }

    .presenceRow {
        display: flex;
        gap: var(--space-6);
        align-items: center;
        min-width: 0;
        white-space: nowrap;
    }

    // ds-deviation: Figma's 4px idle dot, a ring with no token size.
    .presenceRing {
        --tooltip-presence-dot: 4px;

        flex-shrink: 0;
        box-sizing: border-box;
        width: var(--tooltip-presence-dot);
        height: var(--tooltip-presence-dot);
        border: var(--border-width) solid var(--border-default);
        border-radius: 50%;
    }

    .presenceText {
        display: flex;
        gap: var(--space-4);
        align-items: center;
        min-width: 0;
    }

    .presenceName {
        @include tokens.text-xs-12-medium;

        flex-shrink: 0;
    }

    .presenceSeparator {
        flex-shrink: 0;
        color: var(--fg-tertiary);
    }

    .presenceDetail {
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
    }
}
